import React from 'react';
import { GasBadge, useGasI18n } from '../../../shared/kit';
import type { SearchInterpretation } from '../types';

function formatInterval(
    start: string | null | undefined,
    end: string | null | undefined,
    locale: string,
): string {
    if (!start && !end) return '';
    const fmt = (raw: string) => {
        const d = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
        if (Number.isNaN(d.getTime())) return raw;
        return d.toLocaleString(locale, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };
    if (start && end) return `${fmt(start)} → ${fmt(end)}`;
    return fmt(start || end || '');
}

export function InterpretationPanel({
    interpretation,
    locale,
}: {
    interpretation: SearchInterpretation | null | undefined;
    locale: string;
}): React.ReactElement | null {
    const { t } = useGasI18n();
    if (!interpretation) return null;

    const reqs = interpretation.requirements ?? [];
    const interval = interpretation.interval;
    const brands = Array.isArray(interpretation.filters?.brand_required)
        ? (interpretation.filters!.brand_required as string[])
        : [];
    const softBrands = reqs
        .map((r) => r.reference)
        .filter((b): b is string => !!b);

    return (
        <section className="gas-loc-csearch__interp" aria-labelledby="gas-loc-csearch-interp-title">
            <h2 id="gas-loc-csearch-interp-title" className="gas-loc-csearch__section-title">
                {t('location.search.understood_title')}
            </h2>

            {interval?.start || interval?.end ? (
                <p className="gas-loc-csearch__period">
                    <span className="gas-loc-csearch__label">{t('location.search.period')}</span>
                    {' '}
                    <strong>{formatInterval(interval?.start, interval?.end, locale)}</strong>
                    {interval?.timezone ? (
                        <span className="gas-loc-csearch__tz"> ({interval.timezone})</span>
                    ) : null}
                </p>
            ) : (
                <p className="gas-loc-csearch__period gas-loc-csearch__period--missing" role="status">
                    {t('location.search.period_missing')}
                </p>
            )}

            {reqs.length > 0 ? (
                <ul className="gas-loc-csearch__needs">
                    {reqs.map((r, i) => (
                        <li key={`${r.type}-${i}`} className="gas-loc-csearch__need">
                            <GasBadge tone="info">
                                {t('location.search.qty_x', { n: String(r.quantity ?? 1) })}
                            </GasBadge>
                            <span className="gas-loc-csearch__need-text">
                                {r.text || r.type || t('location.search.need_generic')}
                            </span>
                            {r.reference ? (
                                <GasBadge tone="muted">
                                    {t('location.search.brand', { brand: r.reference })}
                                </GasBadge>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="gas-loc-csearch__empty-needs" role="status">
                    {t('location.search.no_needs')}
                </p>
            )}

            {(brands.length > 0 || softBrands.length > 0) && (
                <p className="gas-loc-csearch__brands">
                    <span className="gas-loc-csearch__label">{t('location.search.brands')}</span>
                    {' '}
                    {[...new Set([...brands, ...softBrands])].join(', ')}
                </p>
            )}
        </section>
    );
}
